import { useCallback, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import {
    UrlSlug,
    urlSlugToDataProductKey
} from "@/analytics/mappings/dataProductKey.ts";
import { wacToken } from "@/libraries/design/foundations/index.ts";
import { PricingCards } from "@/libraries/design/patterns/pricing-cards/src/pricing-cards.tsx";
import { useDwellTimeElementAttributes } from "@/utils/dwell-tracking/dwell-tracker-hooks.ts";

import { useCalculationState } from "./calculation.ts";
import { getFeaturesList } from "./components/features-list";
import { PricingCalculatorInputs } from "./components/pricing-calculator-inputs";
import { LoadingSpinner } from "./components/spinner";
import {
    stripJpdHighlightedFeatures,
    useShouldStripJpdHighlightedFeatures
} from "./configs/jpd.pricing-calculator-config.ts";
import { serviceCollectionOfferingSkus } from "./configs/service-collection.pricing-calculator-config.ts";
import {
    GetDiscountedPriceArgs,
    PricingCalculatorContextProvider
} from "./context.tsx";
import { useProviderProps } from "./mappings.ts";
import * as messages from "./messages.ts";
import { PricingCalculatorCard } from "./pricing-calculator-card.tsx";
import { InputValues, Plan } from "./types.ts";

interface RecommendationTrackerProps {
    plan: Plan;
    index: number;
    onRecommendedChange: (index: number, isRecommended: boolean) => void;
}

const RecommendationTracker = ({
    plan,
    index,
    onRecommendedChange
}: RecommendationTrackerProps) => {
    const state = useCalculationState({ offeringSku: plan.fields.sku });

    useEffect(() => {
        onRecommendedChange(index, state.recommended);
    }, [index, state.recommended, onRecommendedChange]);

    return null;
};

export interface UniformPricingCalculatorProps {
    urlSlug: string;
    comparePlansData: Plan[];
    disclaimer?: string;
    inputs?: InputValues;
    onChangeInputs?: (value: InputValues) => void;
    getDiscountedPrice: (args: GetDiscountedPriceArgs) => number;
}

interface PricingCalculatorContentProps {
    comparePlansData: Plan[];
    urlSlug: UrlSlug;
    selectedCardIndex: number | undefined;
    setSelectedCardIndex: (index: number | undefined) => void;
    disclaimer?: string;
    omitInputs?: boolean;
    numSections?: number;
}

const PricingCalculatorContent = ({
    comparePlansData,
    urlSlug,
    selectedCardIndex,
    setSelectedCardIndex,
    disclaimer,
    omitInputs,
    numSections
}: PricingCalculatorContentProps) => {
    const intl = useIntl();

    const handleRecommendedChange = useCallback(
        (index: number, isRecommended: boolean) => {
            if (isRecommended) {
                setSelectedCardIndex(index);
            }
        },
        [setSelectedCardIndex]
    );

    const unitsLabel = useUnitsLabel(comparePlansData);

    const dwellTimeAttributes = useDwellTimeElementAttributes({
        id: "pricing-calculator"
    });

    return (
        <div
            css={css({
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            })}
            {...dwellTimeAttributes}
        >
            {/* this doesn't render anything and just tracks the recommended card only if there are multiple plans */}
            {comparePlansData.length > 1 &&
                comparePlansData.map((planData, index) => (
                    <RecommendationTracker
                        key={`tracker-${planData.fields.sku}`}
                        plan={planData}
                        index={index}
                        onRecommendedChange={handleRecommendedChange}
                    />
                ))}
            {!omitInputs && (
                <PricingCalculatorInputs
                    unitCountLabel={intl.formatMessage(
                        messages.units.teamSizePrompt
                    )}
                    unitsLabel={unitsLabel}
                    dataProductKey={
                        urlSlugToDataProductKey[
                            urlSlug as keyof typeof urlSlugToDataProductKey
                        ]
                    }
                />
            )}
            <PricingCards
                // must set the number of sections here if removing the bottom section
                // could also infer this from planData's getFeaturesList(fields.highlightedFeaturesContainer)
                numSections={numSections}
                selectedCardIndex={
                    selectedCardIndex ? selectedCardIndex + 1 : 1
                }
            >
                {comparePlansData.map((planData, index) => (
                    <PricingCalculatorCard
                        showBannerText={comparePlansData.length > 1}
                        key={planData.fields.sku}
                        planData={planData}
                        urlSlug={urlSlug}
                        index={index}
                        isSingleCard={comparePlansData.length === 1}
                    />
                ))}
            </PricingCards>

            {disclaimer && (
                <div css={disclaimerContainerCss}>
                    <p css={disclaimerCss}>{disclaimer}</p>
                </div>
            )}
        </div>
    );
};

const disclaimerContainerCss = css({
    display: "flex",
    justifyContent: "center"
});

const disclaimerCss = css({
    maxWidth: "384px",
    fontWeight: "400",
    lineHeight: "20px",
    fontSize: "14px",
    color: wacToken("color.primary.black"),
    padding: token("space.150"),
    margin: 0
});

const DEFAULT_SELECTED_CARD_INDEX = 2;

const UniformPricingCalculator = (props: UniformPricingCalculatorProps) => {
    const {
        urlSlug,
        disclaimer,
        inputs,
        onChangeInputs,
        getDiscountedPrice,
        comparePlansData
    } = props;

    // GWP-10907: JPD-specific decision (urlSlug + FF) about whether to strip
    // each plan's `highlightedFeaturesContainer` BEFORE we compute
    // `numSections` and render any cards. The hook itself owns the JPD-only
    // `urlSlug === "product-discovery"` check, so this shared calculator no
    // longer has to know about JPD product semantics. For every non-JPD
    // product the hook returns `false` and the sanitizer is a no-op.
    const shouldStripJpdHighlightedFeatures =
        useShouldStripJpdHighlightedFeatures(urlSlug);

    const [selectedCardIndex, setSelectedCardIndex] = useState<
        number | undefined
    >(DEFAULT_SELECTED_CARD_INDEX);
    const providerResult = useProviderProps({
        plans: comparePlansData,
        product: urlSlug,
        inputValues: inputs
    });

    if (providerResult.status === "pending") {
        return <LoadingSpinner />;
    }

    if (providerResult.status === "error") {
        return null; // todo: fire an event @bsparks GWP-6943
    }

    if (providerResult.status !== "ready") {
        return null; // this should never happen due to checks above, but fixes type error
    }

    const { config, inputValues } = providerResult.props;

    // Resilience guard: drop any plan whose SKU is not represented in the
    // code-side `planLimitConfigs`. Downstream selectors (e.g.
    // `getPlanLimitConfig` in selectors.ts) throw on unknown SKUs, which
    // would crash the entire pricing page via the error boundary. This
    // lets CMS authors stage a new plan/SKU in Contentful before the
    // corresponding code-side limit config exists, without breaking the
    // live page. Once a `planLimitConfigs` entry is added for the SKU the
    // plan will start rendering automatically.
    //
    // For JPD this is also the mechanism that gates the new Enterprise
    // SKU rollout (GWP-10907): when the feature flag is off,
    // `useJpdPlanLimitConfigs()` (in `mappings.ts`) excludes the
    // Enterprise entry, so this guard automatically filters the matching
    // CMS plan out.
    const knownSkus = new Set(
        config.planLimitConfigs.map((plan) => plan.offeringSku)
    );
    const skippedSkus = comparePlansData
        .filter((plan) => !knownSkus.has(plan.fields.sku))
        .map((plan) => plan.fields.sku);
    if (skippedSkus.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
            `[UniformPricingCalculator] Skipping plan(s) with SKUs not present in planLimitConfigs for "${urlSlug}": ${skippedSkus.join(
                ", "
            )}. Add them to the corresponding *.pricing-calculator-config.ts to render.`
        );
    }
    // Drop unknown SKUs first (resilience guard above), then — for JPD with
    // the highlighted-features FF off — strip the per-card highlighted-
    // features content via the JPD-owned sanitizer. For every non-JPD
    // product `shouldStripJpdHighlightedFeatures` is `false`, so the
    // `stripJpdHighlightedFeatures` call is skipped entirely and the
    // filtered `comparePlansData` flows through unchanged.
    const filteredComparePlansData = comparePlansData.filter((plan) =>
        knownSkus.has(plan.fields.sku)
    );
    const sanitizedComparePlansData: Plan[] = shouldStripJpdHighlightedFeatures
        ? stripJpdHighlightedFeatures(filteredComparePlansData)
        : filteredComparePlansData;

    const numSections =
        getFeaturesList(
            sanitizedComparePlansData?.[0]?.fields?.highlightedFeaturesContainer
        ) === undefined
            ? 1
            : 2;

    return (
        <PricingCalculatorContextProvider
            config={config}
            inputValues={inputValues}
            onChangeInputs={onChangeInputs}
            getDiscountedPrice={getDiscountedPrice}
        >
            <PricingCalculatorContent
                comparePlansData={sanitizedComparePlansData}
                urlSlug={urlSlug as UrlSlug}
                selectedCardIndex={selectedCardIndex}
                setSelectedCardIndex={setSelectedCardIndex}
                disclaimer={disclaimer}
                numSections={numSections}
                omitInputs={
                    !config.billingCycleConfig.monthly &&
                    !config.billingCycleConfig.annual
                }
            />
        </PricingCalculatorContextProvider>
    );
};

export default UniformPricingCalculator;

function useUnitsLabel(comparePlansData: Plan[]) {
    const intl = useIntl();

    const hasAgents = comparePlansData.some((plan) => {
        return (
            [
                serviceCollectionOfferingSkus.free,
                serviceCollectionOfferingSkus.standard,
                serviceCollectionOfferingSkus.premium,
                serviceCollectionOfferingSkus.enterprise
            ] as string[]
        ).includes(plan.fields.sku);
    });
    if (hasAgents) {
        return intl.formatMessage(messages.units.agentsUnitsLabel);
    }

    return intl.formatMessage(messages.units.usersUnitsLabel);
}
